import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

export type BadgeTone = 'success' | 'warning' | 'danger' | 'muted' | 'primary' | 'neutral';

const tones: Record<BadgeTone, { box: string; dot: string }> = {
  success: { box: 'bg-success-soft text-success border-success/20', dot: 'bg-success' },
  warning: { box: 'bg-warning-soft text-warning border-warning/25', dot: 'bg-warning' },
  danger: { box: 'bg-danger-soft text-danger border-danger/20', dot: 'bg-danger' },
  muted: { box: 'bg-muted-soft text-muted border-muted/20', dot: 'bg-muted' },
  primary: { box: 'bg-primary-soft text-primary border-primary/20', dot: 'bg-primary' },
  neutral: { box: 'bg-surface text-ink-secondary border-line-strong', dot: 'bg-ink-muted' },
};

interface BadgeProps {
  tone?: BadgeTone;
  /** Shows a leading status dot. The text label always carries the meaning; color is supplementary. */
  dot?: boolean;
  icon?: ReactNode;
  children: ReactNode;
  className?: string;
  title?: string;
}

export function Badge({ tone = 'neutral', dot = false, icon, children, className, title }: BadgeProps) {
  const t = tones[tone];
  return (
    <span
      title={title}
      className={cn(
        'inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-xs font-medium',
        t.box,
        className,
      )}
    >
      {dot && <span aria-hidden="true" className={cn('h-1.5 w-1.5 rounded-full', t.dot)} />}
      {icon}
      {children}
    </span>
  );
}
